import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Cake, PartyPopper, Image as ImageIcon } from 'lucide-react';
import { publicApi } from '@/lib/api';
import { fadeUp, stagger } from '@/lib/motion';
import EventLeadForm from '@/components/public/EventLeadForm';

const ICONS = { cumpleanos: Cake, eventos: PartyPopper };
const HEADER_ACCENT = {
    cumpleanos: 'bg-[hsl(var(--kche-pink))] text-white',
    eventos: 'bg-[hsl(var(--kche-blue))] text-white',
};
const SECTION_LABELS = { cumpleanos: 'Cumpleaños', eventos: 'Eventos' };

export const CumpleEventosSection = ({ withForm = false, source = 'event_form' }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        publicApi.getEvents()
            .then((data) => setItems(Array.isArray(data) ? data : []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    const visibleItems = useMemo(
        () => items.filter((item) => item && (item.title || item.description || item.image_url || item.section_id)),
        [items]
    );

    return (
        <section data-testid="home-cumple-eventos" className="relative bg-background py-16 sm:py-20 lg:py-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="text-center max-w-2xl mx-auto">
                    <p className="text-sm font-bold font-display text-[hsl(var(--kche-purple))] uppercase tracking-wider">Cumpleaños y eventos</p>
                    <h2 className="mt-1 text-3xl sm:text-4xl lg:text-5xl font-display font-bold">Celebra con <span className="text-gradient-fun">nosotros</span></h2>
                    <p className="mt-3 text-muted-foreground text-base sm:text-lg">Coordinemos los detalles juntos. Solicita información y arma una fiesta inolvidable.</p>
                </div>

                {loading ? (
                    <div className="mt-10 grid gap-6 lg:grid-cols-2">
                        {[0, 1].map((i) => (
                            <div key={i} className="h-80 rounded-3xl bg-white border border-border shadow-soft animate-pulse" />
                        ))}
                    </div>
                ) : visibleItems.length === 0 ? (
                    <div className="mt-10 rounded-3xl bg-white border border-dashed border-border p-10 text-center shadow-soft">
                        <div className="mx-auto size-12 rounded-2xl bg-[hsl(var(--kche-yellow))]/25 inline-flex items-center justify-center">
                            <PartyPopper className="size-6 text-[hsl(var(--kche-pink))]" />
                        </div>
                        <h3 className="mt-3 font-display text-xl font-semibold">Pronto agregaremos opciones para celebrar</h3>
                        <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">Los bloques creados desde el panel aparecerán aquí cuando estén visibles.</p>
                    </div>
                ) : (
                    <motion.div initial="hidden" animate="show" variants={stagger} className="mt-10 grid gap-6 lg:grid-cols-2">
                        {visibleItems.map((item) => {
                            const sectionId = item.section_id || 'eventos';
                            const Icon = ICONS[sectionId] || PartyPopper;
                            const label = SECTION_LABELS[sectionId] || 'Evento';
                            const title = item.title || label;
                            const description = item.description || 'Información pendiente de completar desde el panel administrativo.';

                            return (
                                <motion.article key={item.id || `${sectionId}-${title}`} variants={fadeUp} className="rounded-3xl bg-white border border-border overflow-hidden shadow-soft hover:shadow-pop transition-all">
                                    <div className="relative aspect-[16/9] overflow-hidden bg-gradient-candy">
                                        {item.image_url ? (
                                            <img src={item.image_url} alt={title} className="w-full h-full object-cover" loading="lazy" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[hsl(var(--kche-yellow))]/25 via-[hsl(var(--kche-cyan))]/20 to-[hsl(var(--kche-pink))]/25">
                                                <ImageIcon className="size-12 text-[hsl(var(--kche-purple))]/60" />
                                            </div>
                                        )}
                                        <span className={`absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold font-display ${HEADER_ACCENT[sectionId] || HEADER_ACCENT.eventos}`}>
                                            <Icon className="size-3.5" /> {label}
                                        </span>
                                    </div>
                                    <div className="p-6">
                                        <h3 className="text-2xl font-display font-bold text-foreground">{title}</h3>
                                        <p className="mt-2 text-muted-foreground">{description}</p>
                                        {item.bullets?.length > 0 && (
                                            <ul className="mt-3 grid gap-1 text-sm text-foreground/80">
                                                {item.bullets.map((b, i) => (
                                                    <li key={i} className="flex items-center gap-2"><span className="size-1.5 rounded-full bg-[hsl(var(--kche-yellow))]" />{b}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </motion.article>
                            );
                        })}
                    </motion.div>
                )}

                {withForm && (
                    <div className="mt-12" data-testid="cumple-event-form-wrap">
                        <div className="rounded-3xl bg-kche-soft border border-border p-6 sm:p-8 shadow-soft">
                            <h3 className="text-3xl font-display font-bold">Cuéntanos sobre tu evento</h3>
                            <p className="mt-1 text-sm text-muted-foreground">Te contactaremos por tu canal preferido para coordinar.</p>
                            <div className="mt-6"><EventLeadForm source={source} /></div>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default CumpleEventosSection;
